import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Grid,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconClipboardList, IconPlus, IconTrash, IconDeviceFloppy } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const CreateBillOfMaterials = () => {
    const history = useHistory();
    const [appId, setAppId] = useState(null);
    const [products, setProducts] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    const [name, setName] = useState('');
    const [finalProductId, setFinalProductId] = useState('');
    const [finalVariacaoId, setFinalVariacaoId] = useState('');
    const [outputQuantity, setOutputQuantity] = useState('1');
    const [notes, setNotes] = useState('');

    const [componentProductId, setComponentProductId] = useState('');
    const [componentVariacaoId, setComponentVariacaoId] = useState('');
    const [componentQuantity, setComponentQuantity] = useState('');
    const [items, setItems] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/products/`);
                setProducts(res.data || []);
            } catch (error) {
                console.error(error);
            }
        })();
    }, [appId]);

    const finalVariacoes = products.find((p) => String(p.id) === String(finalProductId))?.variacoes || [];
    const componentVariacoes = products.find((p) => String(p.id) === String(componentProductId))?.variacoes || [];

    const handleAddItem = () => {
        if (!componentVariacaoId || !componentQuantity || Number(componentQuantity) <= 0) {
            setStatus({ type: 'error', message: 'Selecione o componente e informe uma quantidade maior que zero' });
            return;
        }
        const product = products.find((p) => String(p.id) === String(componentProductId));
        const variacao = componentVariacoes.find((v) => String(v.id) === String(componentVariacaoId));
        setItems((prev) => [
            ...prev,
            {
                component_variacao: variacao.id,
                label: `${product.nome} - ${variacao.nome_variacao}`,
                quantity: componentQuantity
            }
        ]);
        setComponentProductId('');
        setComponentVariacaoId('');
        setComponentQuantity('');
    };

    const handleRemoveItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!name) {
            setStatus({ type: 'error', message: 'Informe o nome da ficha técnica' });
            return;
        }
        if (!finalVariacaoId) {
            setStatus({ type: 'error', message: 'Selecione o produto final' });
            return;
        }
        if (items.length === 0) {
            setStatus({ type: 'error', message: 'Adicione ao menos um componente' });
            return;
        }
        setSaving(true);
        try {
            await api.post(`v1/apps/${appId}/boms/`, {
                name,
                produto_variacao: finalVariacaoId,
                output_quantity: outputQuantity,
                notes,
                items: items.map((item) => ({ component_variacao: item.component_variacao, quantity: item.quantity }))
            });
            history.push('/dashboard/production/bom');
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar ficha técnica' });
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    return (
        <Box>
            <PageHeader icon={IconClipboardList} gradient="blue" title="Nova Ficha Técnica" subtitle="Receita de produção de um produto" />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField label="Nome da ficha" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Quantidade produzida"
                                type="number"
                                fullWidth
                                value={outputQuantity}
                                onChange={(e) => setOutputQuantity(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                label="Produto final"
                                fullWidth
                                required
                                value={finalProductId}
                                onChange={(e) => {
                                    setFinalProductId(e.target.value);
                                    setFinalVariacaoId('');
                                }}
                            >
                                {products.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.nome}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                label="Variação final"
                                fullWidth
                                required
                                disabled={!finalProductId}
                                value={finalVariacaoId}
                                onChange={(e) => setFinalVariacaoId(e.target.value)}
                            >
                                {finalVariacoes.map((v) => (
                                    <MenuItem key={v.id} value={v.id}>
                                        {v.nome_variacao}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Observações (opcional)" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                        </Grid>
                    </Grid>
                </CardContent>
            </GlassCard>

            <GlassCard sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Componente (produto)"
                                fullWidth
                                value={componentProductId}
                                onChange={(e) => {
                                    setComponentProductId(e.target.value);
                                    setComponentVariacaoId('');
                                }}
                            >
                                {products.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.nome}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                select
                                label="Variação"
                                fullWidth
                                disabled={!componentProductId}
                                value={componentVariacaoId}
                                onChange={(e) => setComponentVariacaoId(e.target.value)}
                            >
                                {componentVariacoes.map((v) => (
                                    <MenuItem key={v.id} value={v.id}>
                                        {v.nome_variacao}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Quantidade"
                                type="number"
                                fullWidth
                                value={componentQuantity}
                                onChange={(e) => setComponentQuantity(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button fullWidth variant="outlined" startIcon={<IconPlus size={16} />} onClick={handleAddItem}>
                                Adicionar
                            </Button>
                        </Grid>
                    </Grid>

                    <Table size="small" sx={{ mt: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Componente</TableCell>
                                <TableCell>Quantidade</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={`${item.component_variacao}-${index}`}>
                                    <TableCell>{item.label}</TableCell>
                                    <TableCell>{item.quantity}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        Nenhum componente adicionado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Button variant="contained" startIcon={<IconDeviceFloppy size={18} />} onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar Ficha Técnica'}
            </Button>
        </Box>
    );
};

export default CreateBillOfMaterials;
